import * as React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CloseIcon from '@mui/icons-material/Close';
import DeselectIcon from '@mui/icons-material/Deselect';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SelectAllIcon from '@mui/icons-material/SelectAll';
import { Translate } from '../../../../../../../shared/LanguageService';
import { ExpandingButton, TRANSITION_DURATION, TRANSITION_EASING } from './ExpandingButton';

export type SelectionAction = {
    label: string;
    icon: React.ReactNode;
    onClick: () => void;
    disabled: boolean;
    disabledTooltip?: string;
    color?: 'primary' | 'error';
    testId?: string;
};

export type SelectAllOption = {
    isAllSelected: boolean;
    onSelectAll: () => void;
    onDeselectAll: () => void;
};

type ImageSelectionToolbarProps = {
    isSelecting: boolean;
    isSelectDisabled?: boolean;
    onSelectStart: () => void;
    onCancel: () => void;
    selectTooltip: string;
    actions: SelectionAction[];
    selectAll?: SelectAllOption;
};

export function ImageSelectionToolbar({
    isSelecting,
    isSelectDisabled = false,
    onSelectStart,
    onCancel,
    selectTooltip,
    actions,
    selectAll
}: ImageSelectionToolbarProps) {
    return (
        <Box
            component="nav"
            role="toolbar"
            aria-label="Image selection actions"
            sx={{
                display: 'flex',
                alignItems: 'center'
            }}
        >
            {actions.map((action) => (
                <Tooltip
                    key={action.testId || action.label}
                    placement="top"
                    title={action.disabled && action.disabledTooltip ? action.disabledTooltip : ''}
                >
                    <span style={{ display: 'inline-flex' }}>
                        <ExpandingButton
                            variant="contained"
                            color={action.color || 'primary'}
                            startIcon={action.icon}
                            disabled={action.disabled}
                            data-visible={isSelecting}
                            data-testid={action.testId}
                            aria-hidden={!isSelecting}
                            tabIndex={isSelecting ? 0 : -1}
                            onClick={action.onClick}
                            sx={{
                                transitionDelay: isSelecting ? '50ms' : '0ms',
                                ...(action.color !== 'error'
                                    ? {
                                          backgroundColor: 'primary.main',
                                          '&:hover': {
                                              backgroundColor: 'primary.dark'
                                          }
                                      }
                                    : {})
                            }}
                        >
                            {action.label}
                        </ExpandingButton>
                    </span>
                </Tooltip>
            ))}

            {selectAll && (
                <ExpandingButton
                    variant="outlined"
                    startIcon={selectAll.isAllSelected ? <DeselectIcon /> : <SelectAllIcon />}
                    data-visible={isSelecting}
                    data-testid="select-all-images"
                    aria-hidden={!isSelecting}
                    tabIndex={isSelecting ? 0 : -1}
                    onClick={
                        selectAll.isAllSelected ? selectAll.onDeselectAll : selectAll.onSelectAll
                    }
                    sx={{ transitionDelay: isSelecting ? '50ms' : '0ms' }}
                >
                    {Translate(
                        selectAll.isAllSelected
                            ? 'image-processing/button-label/select-many/deselect-all'
                            : 'image-processing/button-label/select-many/select-all'
                    )}
                </ExpandingButton>
            )}

            <Tooltip placement="top" title={isSelecting || isSelectDisabled ? '' : selectTooltip}>
                <Button
                    variant="outlined"
                    aria-expanded={isSelecting}
                    startIcon={<CheckBoxOutlineBlankIcon />}
                    disabled={isSelecting || isSelectDisabled}
                    data-testid="select-images"
                    endIcon={
                        <ExpandMoreIcon
                            sx={{
                                transition: `transform ${TRANSITION_DURATION} ${TRANSITION_EASING}`,
                                transform: isSelecting ? 'rotate(180deg)' : 'rotate(0deg)'
                            }}
                        />
                    }
                    onClick={onSelectStart}
                    sx={{
                        whiteSpace: 'nowrap',
                        transition: `all ${TRANSITION_DURATION} ${TRANSITION_EASING}`
                    }}
                >
                    {Translate('image-processing/button-label/select-many/enable')}
                </Button>
            </Tooltip>

            <ExpandingButton
                variant="outlined"
                startIcon={<CloseIcon />}
                data-visible={isSelecting}
                aria-hidden={!isSelecting}
                tabIndex={isSelecting ? 0 : -1}
                onClick={onCancel}
                sx={{ transitionDelay: isSelecting ? '50ms' : '0ms' }}
            >
                {Translate('image-processing/button-label/select-many/disable')}
            </ExpandingButton>
        </Box>
    );
}
